import { defaultFeatureConfig, FeatureConfigKey } from './defaultFeatureConfig';

/**
 * Writes default feature configuration values to the session storage.
 *
 * Iterates over each entry in the `defaultFeatureConfig` object and checks if the corresponding key
 * is already present in the session storage. If the key does not exist, it stores the value (converted to a string)
 * under that key in the session storage.
 *
 * This function ensures that only missing configuration keys are added, preserving any existing values.
 */
function writeFeatureConfigToSessionStorage() {
  Object.entries(defaultFeatureConfig).forEach(([key, value]) => {
    if (sessionStorage.getItem(key) === null) {
      sessionStorage.setItem(key, value.toString());
    }
  });
}

/**
 * Checks if a feature is active by retrieving its value from sessionStorage.
 *
 * @param key - The key corresponding to the feature configuration.
 * @returns `true` if the feature is active (i.e., the value in sessionStorage is 'true'), otherwise `false`.
 */
function isFeatureActive(key: FeatureConfigKey) {
  return sessionStorage.getItem(key) === 'true';
}

/**
 * Sets the state of a feature flag in the session storage.
 *
 * @param key - The key representing the feature configuration.
 * @param value - The boolean value to set for the feature flag.
 */
export function setFeatureState(key: FeatureConfigKey, value: boolean) {
  sessionStorage.setItem(key, value.toString());
}

/**
 * Retrieves the current feature configuration from session storage.
 *
 * This function first ensures that the feature configuration is written to session storage.
 * It then constructs and returns an object mapping each feature key to a boolean indicating
 * whether the feature is active, based on the current session state.
 *
 * @returns An object where each key is a feature config key and the value is a boolean indicating if the feature is active.
 */
export function getFeatureConfigFromSessionStorage() {
  writeFeatureConfigToSessionStorage();

  const sessionFeatureConfig = Object.keys(defaultFeatureConfig).reduce(
    (acc, key) => {
      const featureKey = key as FeatureConfigKey;

      acc[featureKey] = isFeatureActive(featureKey);

      return acc;
    },
    {} as Record<FeatureConfigKey, boolean>,
  );

  return sessionFeatureConfig;
}
